import { SandpackCodeEditor, SandpackProvider, useSandpack } from '@codesandbox/sandpack-react'
import { Flatfile } from '@flatfile/api-internal'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  Text,
} from '@flatfile/baseline'
import JSZip from 'jszip'
import { useCallback, useEffect, useState } from 'react'
import usePromise from 'react-promise-suspense'
import { useNavigate, useParams } from 'react-router-dom'
import * as XLSX from 'xlsx'
import { AppHTTPService } from '../../api/http-service'
import { getNavigationPath } from '../../components/Apps/AppCTAs'
import { makeBlueprint } from '../../components/Blueprint/fromFile/blueprint'
import { generateSetup } from '../../components/Blueprint/fromSchema/schemaBlueprint'
import CreateNewApp from '../../components/CreateNewApp/CreateNewApp'
import { useDynamicHeight } from '../../hooks/useDynamicHeight'
import { useActiveEnvironment } from '../../hooks/useEnvironments'
import { useIdentity } from '../../hooks/useIdentity'
import useResource from '../../resources/hooks/useResource'
import useResourceList from '../../resources/hooks/useResourceList'
import { useCreateResource, useUpdateResource } from '../../resources/hooks/useResourceMutation'
import { logger } from '../../utils/logging'
import { spacesUrl } from '../../utils/pageUtils'

// Timeline step component
interface TimelineStepProps {
  isActive: boolean
  isComplete: boolean
  stepNumber: number
  icon: string
  title: string
  children: React.ReactNode
  buttons?: React.ReactNode
}

const TimelineStep = ({ isComplete, stepNumber, title, children, buttons }: TimelineStepProps) => {
  return (
    <div className='relative mb-4'>
      <div className='flex'>
        <div className='flex items-center justify-center w-8 -ml-4 mr-2 flex-shrink-0 self-start h-8 bg-transparent text-primary'>
          {isComplete ? (
            <Icon name='checkmark-2' size={16} />
          ) : (
            <span className='flex items-center justify-center text-lg -mt-2 leading-none font-medium'>
              {stepNumber}
            </span>
          )}
        </div>
        <div className='flex-1'>
          <Text variant='title3' emphasized className='mb-2'>
            {title}
          </Text>
          {children}
        </div>
      </div>
      {buttons && <div className='w-full flex flex-wrap justify-start gap-2 mt-6 ml-6'>{buttons}</div>}
    </div>
  )
}

const exampleBlueprint = {
  name: 'All Data',
  labels: ['pinned'],
  sheets: [
    {
      name: 'Contacts',
      slug: 'contacts',
      fields: [
        {
          key: 'firstName',
          type: 'string',
          label: 'First Name',
        },
        {
          key: 'lastName',
          type: 'string',
          label: 'Last Name',
        },
        {
          key: 'email',
          type: 'string',
          label: 'Email',
        },
      ],
    },
  ],
}

const blueprintSeed = `const blueprint = ${JSON.stringify(exampleBlueprint, null, 2)}`

const sandpackOpts = {
  activeFile: 'index.js',
  visibleFiles: ['index.js', 'blueprint.js'],
}

const getCurrentApp = (identifier: { id?: string; namespace?: string }) => {
  if (identifier.id) {
    return useResource('APPS', identifier.id)
  }
  if (identifier.namespace) {
    return useResourceList('APPS', {}).response.data.filter(
      (app: Flatfile.App) => app.namespace === identifier.namespace
    )[0]
  }
  return null
}

const getSecretKey = async (environmentId: string) => {
  const apiKeys = await AppHTTPService.client.auth.getApiKeys({ environmentId })
  const secretKey = apiKeys.data.find((key) => key.type === 'SECRET')?.rawKey
  if (!secretKey) {
    throw new Error('Secret key not found')
  }
  return secretKey
}
export default function CustomAppsCreatePage() {
  const { environment: activeEnvironment } = useActiveEnvironment()
  const secretKey = usePromise(getSecretKey, [activeEnvironment!.id])
  const { id = '' } = useParams()
  const { namespace } = useParams()
  const currentApp = getCurrentApp({ id, namespace })
  const [updateApp] = useUpdateResource('APPS')
  const [createSpace] = useCreateResource('SPACES')
  const { identity } = useIdentity()
  const nav = useNavigate()

  const { listHeight } = useDynamicHeight()
  const boxHeight = listHeight - 70

  const createNewSpace = async () => {
    try {
      const newSpace = await createSpace({
        autoConfigure: true,
        name: `My first ${currentApp!.namespace} ${currentApp?.entity.toLowerCase()}`,
        namespace: currentApp!.namespace,
        labels: [currentApp!.namespace],
        environmentId: activeEnvironment!.id,
        appId: currentApp!.id,
      })
      const spaceRootUrl = spacesUrl(newSpace?.id ?? '', identity.token)
      window.open(spaceRootUrl, '_blank', 'noopener,noreferrer')
    } catch (e) {
      logger.error('Failed to create space', { error: e })
    }
  }

  /* eslint-disable no-useless-escape */
  const jsListenerSeed = `import api from '@flatfile/api'
import { recordHook } from '@flatfile/plugin-record-hook'
import { exportWorkbookPlugin } from '@flatfile/plugin-export-workbook'
import { blueprint } from './blueprint'

export default function flatfileEventListener(listener) {
  listener.on('**', (event) => {
    console.log('Event Received: ' + event.topic);
  })

  listener.namespace(namespace, (namespacedEvents) => {
    namespacedEvents.filter({ job: 'space:configure' }, (configure) => {
      configure.on(
        'job:ready',
        async ({ context: { spaceId, environmentId, jobId } }) => {
          try {
            await api.jobs.ack(jobId, {
              info: 'Creating Space',
              progress: 10,
            })

            await api.workbooks.create({
              spaceId,
              environmentId,
              ...blueprint,
            })

            await api.jobs.complete(jobId, {
              outcome: {
                message: 'Space Created',
                acknowledge: true,
              },
            })
          } catch (error) {
            await api.jobs.fail(jobId, {
              outcome: {
                message:
                  'Space Creation Failed. See Event Logs',
                acknowledge: true,
              },
            })
          }
        }
      )
    })

    namespacedEvents.use(
      recordHook('contacts', (record) => {
        const value = record.get('firstName')
        if (typeof value === 'string') {
          record.set('firstName', value.toLowerCase())
        } else {
          record.addError('firstName', 'Invalid first name')
        }

        const email = record.get("email");
        const validEmailAddress = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!validEmailAddress.test(email)) {
          record.addError("email", "Invalid email address")
        }

        return record
      })
    )

    namespacedEvents.use(exportWorkbookPlugin())
  })
}`

  const tsListenerSeed = `import api from '@flatfile/api'
import { FlatfileRecord, recordHook } from '@flatfile/plugin-record-hook'
import { exportWorkbookPlugin } from '@flatfile/plugin-export-workbook'
import { FlatfileEvent } from '@flatfile/listener'
import { blueprint } from './blueprint'

export default function flatfileEventListener(listener) {
  listener.on('**', (event: FlatfileEvent) => {
    console.log('Event Received: ' + event.topic);
  })

  listener.namespace(namespace, (namespacedEvents) => {
    namespacedEvents.filter({ job: 'space:configure' }, (configure) => {
      configure.on(
        'job:ready',
        async ({ context: { spaceId, environmentId, jobId } }: FlatfileEvent) => {
          try {
            await api.jobs.ack(jobId, {
              info: 'Creating Space',
              progress: 10,
            })

            await api.workbooks.create({
              spaceId,
              environmentId,
              ...blueprint,
            })

            await api.jobs.complete(jobId, {
              outcome: {
                message: 'Space Created',
                acknowledge: true,
              },
            })
          } catch (error) {
            await api.jobs.fail(jobId, {
              outcome: {
                message:
                  'Space Creation Failed. See Event Logs',
                acknowledge: true,
              },
            })
          }
        }
      )
    })

    namespacedEvents.use(
      recordHook('contacts', (record) => {
        const value = record.get('firstName')
        if (typeof value === 'string') {
          record.set('firstName', value.toLowerCase())
        } else {
          record.addError('firstName', 'Invalid first name')
        }

        const email = record.get("email")
        const validEmailAddress = /^[^s@]+@[^s@]+.[^s@]+$/

        if (typeof email !== 'string' || email !== null) {
          record.addError("email", 'Invalid email address')
        } else if (!validEmailAddress.test(email)) {
            record.addError("email", 'Invalid email address')
          }

        return record
      })
    )

    namespacedEvents.use(exportWorkbookPlugin())
  })
}`
  /* eslint-enable no-useless-escape */

  const [blueprint, setBlueprint] = useState(blueprintSeed)
  const [language, setLanguage] = useState('javascript')
  const [listener, setListener] = useState(jsListenerSeed)
  const [tsListener, setTsListener] = useState(tsListenerSeed)
  const [activeStep, setActiveStep] = useState(0)
  const [sandpackOptions, setSandpackOptions] = useState(sandpackOpts)
  const [uploadedFile, setUploadedFile] = useState('')
  const [proceedWithTemplate, setProceedWithTemplate] = useState(false)

  if (currentApp?.metadata?.autobuild) {
    nav(getNavigationPath(currentApp))
  }

  useEffect(() => {
    setSandpackOptions((current) => {
      let visibleFiles: string[] = []
      let activeFile: string = ''

      if (language === 'javascript') {
        visibleFiles = ['index.js', 'blueprint.js']
        activeFile = activeStep === 1 ? 'blueprint.js' : 'index.js'
      }
      if (language === 'typescript') {
        visibleFiles = ['index.ts', 'blueprint.js']
        activeFile = activeStep === 0 ? 'blueprint.js' : 'index.ts'
      }
      return { ...current, visibleFiles, activeFile }
    })
  }, [language])

  useEffect(() => {
    setSandpackOptions((current) => {
      let activeFile: string = ''

      activeFile = activeStep === 0 ? 'blueprint.js' : language === 'javascript' ? 'index.js' : 'index.ts'

      return { ...current, activeFile }
    })
  }, [activeStep])

  const ActiveFileUpdate = () => {
    const { sandpack } = useSandpack()
    const { files, activeFile, editorState } = sandpack

    if (editorState === 'dirty') {
      if (activeFile === '/index.js') {
        setListener(files['/index.js'].code)
      }
      if (activeFile === '/index.ts') {
        setTsListener(files['/index.ts'].code)
      }
      if (activeFile === '/blueprint.js') {
        if (uploadedFile === '') {
          setBlueprint(files['/blueprint.js'].code)
        } else if (uploadedFile !== '') {
          setBlueprint(uploadedFile)
          setUploadedFile('')
          setProceedWithTemplate(true)
        }
      }
    }
    return null
  }

  // Helper Functions
  const nextStep = () => setActiveStep((current) => (current < 5 ? current + 1 : current))
  const prevStep = () => setActiveStep((current) => (current > 0 ? current - 1 : current))

  const handleFileUpload = (files: File[]): void => {
    if (files.length === 0) return

    const file = files[0]
    const deconstructedFilename = file.name.split('.')
    const fileType = deconstructedFilename[deconstructedFilename.length - 1]

    const reader = new FileReader()

    reader.onload = async (event) => {
      const binaryStr = event?.target?.result
      if (binaryStr) {
        if (['xlsx', 'xls', 'csv'].includes(fileType)) {
          const uploadData = XLSX.read(binaryStr, {
            type: 'binary',
            dense: true,
            cellDates: true,
          })
          const blup = makeBlueprint(uploadData, {})
          const update = `const blueprint = ${JSON.stringify({ ...blup, name: deconstructedFilename[0] }, null, 2)}`
          setBlueprint(update)
          setUploadedFile(update)
        } else if (['json'].includes(fileType)) {
          const { workbooks } = await generateSetup([{ data: JSON.parse(binaryStr.toString()) }], {})

          const update = `const blueprint = ${JSON.stringify(
            {
              name: workbooks[0].name,
              slug: workbooks[0].name.replace(/\s/g, '').toLowerCase(),
              fields: workbooks[0].sheets[0].fields.map(({ key, label, type }) => {
                // Bug in enum type, hack for now
                return {
                  key,
                  label,
                  type: type === 'enum' ? 'string' : type,
                }
              }),
            },
            null,
            2
          )}`
          setBlueprint(update)
          setUploadedFile(update)
        }
      }
    }

    reader.readAsBinaryString(file)
  }

  const activateApp = useCallback(() => {
    if (currentApp) {
      const now = new Date()
      updateApp(currentApp.id, {
        activatedAt: now,
      })
    }
  }, [])

  const files = {
    '/index.js': {
      code: listener,
    },
    '/index.ts': {
      code: tsListener,
    },
    '/blueprint.js': {
      code: blueprint,
    },
    '/tsconfig.json': {
      code: `{
        "compilerOptions": {
          "target": "es2020",
          "moduleResolution": "node"
        }
      }`,
      hidden: true,
    },
  }

  const GetFiles = async () => {
    const zip = new JSZip()
    const zipList = {} as Record<string, any>

    if (language === 'javascript') {
      zipList['index.js'] = `const namespace = ['space:${currentApp!.namespace}']
${listener}`
      zipList['package.json'] = `{
  "name": "FlatfileApp",
  "dependencies": {
    "@flatfile/api": "1.9.20",
    "@flatfile/plugin-record-hook": "1.7.1",
    "@flatfile/plugin-export-workbook": "^1.0.1",
    "@flatfile/listener": "1.1.0"
  }
}`
    }

    if (language === 'typescript') {
      zipList['index.ts'] = `const namespace = ['space:${currentApp!.namespace}']
${tsListener}`
      zipList['tsconfig.json'] = `{
  "compilerOptions": {
    "target": "es2020",
    "moduleResolution": "node",
    "allowSyntheticDefaultImports": true,
  }
}`
      zipList['package.json'] = `{
  "name": "FlatfileApp",
  "dependencies": {
    "@flatfile/api": "1.9.20",
    "@flatfile/plugin-record-hook": "1.7.1",
    "@flatfile/plugin-export-workbook": "^1.0.1",
    "@flatfile/listener": "1.1.0"
  }
}`
    }

    zipList['blueprint.js'] = `export ${blueprint}`
    zipList['.env'] = `FLATFILE_API_KEY="${secretKey}"
FLATFILE_ENVIRONMENT_ID="${activeEnvironment!.id}" # env: ${activeEnvironment!.name}`

    Object.entries(zipList).forEach(([fileName, fileContent]) => {
      zip.file(fileName, fileContent)
    })

    zip.generateAsync({ type: 'blob' }).then((content) => {
      const url = window.URL.createObjectURL(content)
      const link = document.createElement('a')
      link.href = url
      link.download = 'FlatfileApp.zip'
      link.click()
    })

    activateApp()

    setActiveStep(3)
  }

  return (
    <div className='w-full h-full flex flex-col overflow-auto'>
      {!currentApp && <CreateNewApp mini={false} autoOpen />}
      <div className='grid grid-cols-12 gap-6 p-3'>
        <div className='col-span-5 p-3'>
          <Text variant='title2'>Your use case, in Flatfile.</Text>
          <div className='pt-6'>
            <div className='overflow-y-auto pr-6' style={{ height: `${boxHeight}px` }}>
              <div className='relative ml-1 flex flex-col'>
                {/* Step 1 */}
                <TimelineStep
                  isActive={activeStep >= 0}
                  isComplete={activeStep > 0}
                  stepNumber={1}
                  icon='pencil'
                  title='Create a schema'
                  buttons={
                    activeStep === 0 && (
                      <>
                        <Button variant='secondary' onClick={nextStep}>
                          {proceedWithTemplate ? 'Continue with Blueprint' : 'Continue with demo Blueprint'}
                        </Button>
                        <Button
                          variant='outline'
                          onClick={() => {
                            activateApp()
                            updateApp(currentApp!.id, { activatedAt: new Date() })
                            nav(`/apps/custom/${currentApp!.id}/list/`)
                          }}
                        >
                          Skip tutorial, I'm already setup
                        </Button>
                      </>
                    )
                  }
                >
                  {activeStep > 0 ? (
                    <div>
                      <Text variant='callout' className='text-text-default'>
                        <code className='rounded bg-gray-200 px-1 py-0.5 font-mono text-sm'>blueprint.js</code>
                      </Text>
                    </div>
                  ) : (
                    <div>
                      <Text variant='callout' className='text-text-default'>
                        Transform your schema into a Flatfile Blueprint, a dedicated data description schema designed to
                        prioritize data validation and preparation.
                      </Text>
                      <div
                        className='border border-gray-300 border-dashed rounded p-4 cursor-pointer hover:border-primary transition-colors mt-4'
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault()
                          const fileList = e.dataTransfer?.files
                          if (fileList && fileList.length > 0) {
                            handleFileUpload(Array.from(fileList))
                          }
                        }}
                        onClick={() => {
                          const input = document.createElement('input')
                          input.type = 'file'
                          input.accept = '.csv,.xlsx,.xls,.json'
                          input.onchange = (e) => {
                            const fileList = (e.target as HTMLInputElement).files
                            if (fileList && fileList.length > 0) {
                              handleFileUpload(Array.from(fileList))
                            }
                          }
                          input.click()
                        }}
                        onKeyDown={(evt) => {
                          if (evt.key === 'Enter' || evt.key === ' ') {
                            evt.preventDefault()
                            const input = document.createElement('input')
                            input.type = 'file'
                            input.accept = '.csv,.xlsx,.xls,.json'
                            input.onchange = (e) => {
                              const fileList = (e.target as HTMLInputElement).files
                              if (fileList && fileList.length > 0) {
                                handleFileUpload(Array.from(fileList))
                              }
                            }
                            input.click()
                          }
                        }}
                        role='button'
                        tabIndex={0}
                      >
                        <div className='flex flex-col items-center justify-center pointer-events-none text-center'>
                          <Text variant='body' emphasized className='text-text-default flex flex-col gap-2 mb-2'>
                            Drop a file here or click to upload
                            <Button variant='primary' className='mt-2' icon='arrow-out-of-box' iconPlacement='left'>
                              Upload
                            </Button>
                          </Text>

                          <Text className='my-2 text-text-default'>Accepts file types</Text>
                          <div className='flex justify-center space-x-4'>
                            <div className='h-6 flex items-center justify-center text-primary'>
                              <span className='text-xs font-mono'>XLSX</span>
                            </div>
                            <div className='h-6 flex items-center justify-center text-primary'>
                              <span className='text-xs font-mono'>JSON</span>
                            </div>
                            <div className='h-6 flex items-center justify-center text-primary'>
                              <span className='text-xs font-mono'>CSV</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className='h-4'></div>
                      <Text variant='callout' className='!text-xxs text-muted'>
                        Let Flatfile generate a Blueprint from a data sample by uploading a CSV, XLS, or JSON file.
                        Files should have a header row or at least one level of nesting so that the schema can be
                        inferred. You can also code your own schema directly in the editor.
                      </Text>
                    </div>
                  )}
                </TimelineStep>

                {/* Step 2 */}
                <TimelineStep
                  isActive={activeStep >= 1}
                  isComplete={activeStep > 1}
                  stepNumber={2}
                  icon='code'
                  title='Create a listener'
                  buttons={
                    activeStep === 1 && (
                      <>
                        <Button variant='secondary' onClick={prevStep}>
                          Back
                        </Button>
                        <Button variant='primary' onClick={nextStep}>
                          Continue with{' '}
                          {language && language.length
                            ? language.charAt(0).toUpperCase() + language.slice(1)
                            : 'Javascript'}
                        </Button>
                      </>
                    )
                  }
                >
                  {activeStep > 1 ? (
                    <div>
                      <Text variant='callout' className='text-text-default'>
                        <code className='rounded bg-gray-200 px-1 py-0.5 font-mono text-sm'>
                          {language === 'javascript' ? 'index.js' : 'index.ts'}
                        </code>
                      </Text>
                    </div>
                  ) : activeStep === 1 ? (
                    <div>
                      <Text variant='callout' className='text-text-default'>
                        This example listener will filter events to the <strong>{currentApp!.namespace}</strong>{' '}
                        namespace. Without further customization, it will:
                      </Text>
                      <ul className='list-disc pl-5 mt-2 space-y-1 text-text-default text-sm'>
                        <li>Add a Workbook to a new space</li>
                        <li>Transform and validate records</li>
                        <li>Log your data upon submission</li>
                      </ul>
                      <div className='h-4'></div>
                      <Text variant='footnote' className='text-xs text-text-default'>
                        Choose a language:
                      </Text>
                      <div className='flex gap-2 mt-1'>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant='outline' icon='chevron-bottom' iconPlacement='right' className='min-w-32'>
                              {language.charAt(0).toUpperCase() + language.slice(1)}{' '}
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem onClick={() => setLanguage('javascript')}>Javascript</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setLanguage('typescript')}>Typescript</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ) : null}
                </TimelineStep>

                {/* Step 3 */}
                <TimelineStep
                  isActive={activeStep >= 2}
                  isComplete={activeStep > 2}
                  stepNumber={3}
                  icon='file-download'
                  title='Download your code snippet'
                  buttons={
                    activeStep === 2 && (
                      <>
                        <Button variant='secondary' onClick={prevStep}>
                          Back
                        </Button>
                        <Button variant='primary' onClick={GetFiles} icon='file-download' iconPlacement='left'>
                          Download
                        </Button>
                      </>
                    )
                  }
                >
                  {activeStep > 2 ? (
                    <div>
                      <Text variant='callout' className='text-text-default'>
                        <code className='rounded bg-gray-200 px-1 py-0.5 font-mono text-sm'>FlatfileApp.zip</code>
                      </Text>
                    </div>
                  ) : activeStep === 2 ? (
                    <div>
                      <Text variant='callout' className='text-text-default'>
                        Get your listener, blueprint and supporting configuration files.
                      </Text>
                    </div>
                  ) : null}
                </TimelineStep>

                {/* Step 4 */}
                <TimelineStep
                  isActive={activeStep >= 3}
                  isComplete={activeStep > 3}
                  stepNumber={4}
                  icon='terminal'
                  title='Run your listener'
                  buttons={
                    activeStep === 3 && (
                      <>
                        <Button variant='secondary' onClick={prevStep}>
                          Back
                        </Button>
                        <Button variant='primary' onClick={nextStep}>
                          Continue, it's running
                        </Button>
                      </>
                    )
                  }
                >
                  {activeStep > 3 ? (
                    <div>
                      <Text variant='callout' className='text-text-default'>
                        ✓ Connected to event stream
                      </Text>
                    </div>
                  ) : activeStep === 3 ? (
                    <div>
                      <Text variant='callout' className='text-text-default'>
                        Locate FlatfileApp.zip and unzip your bundle.
                      </Text>
                      <div className='h-2'></div>
                      <Text variant='callout' className='text-text-default'>
                        Change into the directory of your unzipped folder and run the following commands to install
                        dependencies and run your listener.
                      </Text>
                      <div className='h-2'></div>
                      <div className='bg-gray-200 text-white p-3 rounded font-mono text-sm'>
                        npm i && npx flatfile develop
                      </div>
                      <div className='h-4'></div>
                      <Text variant='callout' className='text-text-default'>
                        If your listener is running properly, you should see:
                        <br />
                        <b>✓ Connected to event stream</b>
                      </Text>
                    </div>
                  ) : null}
                </TimelineStep>

                {/* Step 5 */}
                <TimelineStep
                  isActive={activeStep >= 4}
                  isComplete={activeStep > 4}
                  stepNumber={5}
                  icon='star'
                  title={`Create a ${currentApp?.entity}`}
                  buttons={
                    activeStep >= 4 && (
                      <>
                        <Button variant='secondary' onClick={prevStep}>
                          Back
                        </Button>
                        <Button
                          variant='primary'
                          onClick={() => {
                            createNewSpace()
                            setTimeout(() => nav(`/apps/custom/${currentApp!.id}/list/`), 1000)
                          }}
                        >
                          Create a new {currentApp?.entity}
                        </Button>
                      </>
                    )
                  }
                >
                  {activeStep >= 4 && (
                    <div>
                      <Text variant='callout' className='text-text-default'>
                        Now that your listener is running, let's create a new Space from the Spaces list page and watch
                        your listener respond to events.
                      </Text>
                    </div>
                  )}
                </TimelineStep>
              </div>
            </div>
          </div>
        </div>

        <div className='col-span-7'>
          <SandpackProvider
            theme={{
              colors: {
                surface1: '#ffffff',
                surface2: 'var(--color-gray-4)',
                surface3: '#efeff3',
                clickable: '#000',
                disabled: '#C5C5C5',
                hover: '#4D4D4D',
                accent: '#3B2FC9',
                error: '#ff453a',
                errorSurface: '#ffeceb',
              },
              font: {
                body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'",
                mono: "'PT Mono', 'DejaVu Sans Mono', Menlo, Consolas, 'Liberation Mono', Monaco, 'Lucida Console', monospace",
                size: '13px',
                lineHeight: '20px',
              },
            }}
            files={files}
            options={sandpackOptions}
            style={{ border: '1px solid var(--color-gray-4)' }}
          >
            {/* Activate Flatfile is an invisible component that uses Sandpack hooks */}
            <ActiveFileUpdate />
            <SandpackCodeEditor
              showRunButton={false}
              showTabs
              style={{
                height: 'calc(100vh - 6rem)',
              }}
            />
          </SandpackProvider>
        </div>
      </div>
    </div>
  )
}
